import {Injectable} from '@angular/core';
import {Paginator} from '../../models/paginator'
import {HttpClient} from '@angular/common/http'
import {EntitiesService} from '../entities/entities.service'
import {fromPromise} from 'rxjs-compat/observable/fromPromise'
import {EmployeesService} from '../employees/employees.service'
import {Employee} from '../../models/employee'
import {PeopleService} from '../people/people.service'
import {concatMap, map, tap} from 'rxjs/operators'
import {LocationsService} from '../locations/locations.service'
import {BehaviorSubject, Observable, Subject} from 'rxjs'
import {VineaLocation} from '../../models/vinea-location'
import {GroupsService} from '../groups/groups.service'
import {Group} from '../../models/group'
import {Builder} from '../../models/builder'
import {Entity} from '../../models/entity';
import {ObservableApiService} from '../api/observable-api.service'

@Injectable({
  providedIn: 'root'
})
export class ApiVirtualizationLayerService {
  public cacheExpirationTimeSeconds = 60;
  public locations$                 = new BehaviorSubject<Array<Location>>([]);
  private locations$LastAccessTime  = 0;

  public selectedEntities = [];
  public messageBody = '';
  public cloudinaryResults = [];

  constructor(
    private http: HttpClient,
    private entities: EntitiesService,
    private employee: EmployeesService,
    private people: PeopleService,
    private location: LocationsService,
    private group: GroupsService,
    private api2: ObservableApiService,
  ) {
    this.loadSavedCaches();
  }

  loadSavedCaches() {
    if (localStorage.locations$) {
      this.locations$.next(JSON.parse(localStorage.locations$));
      this.locations$LastAccessTime = JSON.parse(localStorage.locations$LastAccessTime);
    }
  }

  save(key, val) {
    localStorage.setItem(key, JSON.stringify(val));
  }

  /**
   * Gets all the entities.
   * ex. children
   */
  getAllEntities() {
    return fromPromise(this.entities.everything());
  }

  getEmployee(id) {
    return fromPromise(this.employee.get(id));
  }

  getPerson(employee: Employee) {
    return fromPromise(this.people.get(employee.person_id));
  }

  getCompleteEmployeeData(id) {
    let employee, employeePerson;
    return this.getEmployee(id).pipe(
      tap((e) => employee = e),
      concatMap(() => {
        return this.getPerson(employee);
      }),
      tap(p => employeePerson = p),
      map(() => ({employee: employee, person: employeePerson}))
    );
  }

  getAllLocations(builder = new Builder()): Observable<Array<VineaLocation>> {
    const locations = fromPromise(this.location.everything(builder));
    locations.subscribe((locs: Array<Location>) => {
      this.locations$.next(locs);
      this.save('locations', locs);
      this.locations$LastAccessTime = new Date().getTime();
    })
    return locations as Observable<Array<VineaLocation>>;
  }

  getAllLocations$(builder = new Builder()) {
    if (this.isExpired(this.locations$LastAccessTime)) {
      const locations = fromPromise(this.location.everything(builder));
      locations.subscribe((locs: Array<Location>) => {
        this.locations$.next(locs);
        this.save('locations$', locs);
        this.save('locations$LastAccessTime', new Date().getTime());
      })
    }
    return this.locations$;
  }

  getGroups(builder: Builder): Observable<Paginator<Group>> {
    return this.group.fromBuilderObserver(builder) as Observable<Paginator<Group>>;
  }

  getAllGroups(builder: Builder): Observable<Array<Group>> {
    return fromPromise(this.group.everything(builder));
  }

  getEntities(builder: Builder): Observable<Paginator<Entity>> {
    return fromPromise(this.entities.fromBuilder(builder)) as Observable<Paginator<Entity>>;
  }

  getEntitiesFromGroup(groupId, builder: Builder): Observable<Paginator<Entity>> {
    return this.api2.get('v1/groups/' + groupId + '/entities?' + builder.buildUrl()) as Observable<Paginator<Entity>>;
  }

  getEntitiesFromLocation(locationId, builder): Observable<Paginator<Entity>> {
    return this.api2.get('v1/locations/' + locationId + '/entities?' + builder.buildUrl()) as Observable<Paginator<Entity>>;
  }

  getStatistics() {
    return this.api2.get('v1/statistics');
  }

  saveEmployee(employee: Employee) {
    return fromPromise(this.employee.save(employee));
  }

  private isExpired(lastAccessTime) {
    const time = new Date().getTime();
    if (time - (this.cacheExpirationTimeSeconds * 1000) > lastAccessTime) {
      return true;
    } else {
      return false;
    }
  }
}
